<script setup>
import { computed, watch, ref, onMounted } from 'vue';
import { useProjeto } from '@/modules/projeto/composables/useProjeto';
import SmallTitle from '@/components/elementos/SmallTitle/SmallTitle.vue';

const { metas, carregarMetas } = useProjeto();

const props = defineProps({
    modelValue: {
        type: Object,
        default: null
    },
    projetoId: {
        type: [String, Number],
        required: true
    },
    error: {
        type: String,
        default: null
    },
    label: {
        type: String,
        default: 'Meta'
    },
    placeholder: {
        type: String,
        default: 'Selecione a meta do projeto'
    }
});

const emit = defineEmits(['update:modelValue', 'change', 'temMeta']);

const loading = ref(false);
const temMeta = computed(() => metas.value && metas.value.length > 0);
const metaSelecionada = ref(null);


// Observar mudanças no modelValue para sincronizar (útil na edição)
watch(() => props.modelValue, (newValue) => {
    if (newValue && newValue !== metaSelecionada.value) {
        metaSelecionada.value = newValue;
    } else if (newValue === null && metaSelecionada.value !== null) {
        metaSelecionada.value = null;
    }
});

// Observar mudanças no projetoId para recarregar metas
watch(() => props.projetoId, async (newValue) => {
    if (newValue) {
        await carregarMetasDoProjeto();
    }
});

const carregarMetasDoProjeto = async () => {
    if (!props.projetoId) return;

    loading.value = true;
    try {
        await carregarMetas(props.projetoId);

        // Emitir evento informando se tem metas
        emit('temMeta', temMeta.value);

        // Se estiver em modo edição e houver um valor selecionado
        if (props.modelValue && temMeta.value) {
            const metaEncontrada = metas.value.find(meta => meta.id === props.modelValue.id);
            if (metaEncontrada) {
                metaSelecionada.value = metaEncontrada;
            }
        }
    } finally {
        loading.value = false;
    }
};

const handleChange = () => {
    emit('update:modelValue', metaSelecionada.value);
    emit('change', metaSelecionada.value);
};

onMounted(async () => {
    await carregarMetasDoProjeto();
});
</script>

<template>
    <div v-if="temMeta" class="field mb-2 col-12">
        <SmallTitle :titulo="label" :obrigatorio="true" />
        <Dropdown
            inputId="meta_input"
            v-model="metaSelecionada"
            :options="metas"
            optionLabel="descricao"
            :placeholder="placeholder"
            emptyFilterMessage="Nenhum item encontrado"
            class="text-sm"
            :class="{ 'p-invalid': error }"
            :loading="loading"
            :disabled="loading"
            @change="handleChange"
            :pt="{
                emptyFilterMessage: { class: 'text-sm' },
            }">
            <template #value="slotProps">
                <div v-if="slotProps.value" class="flex align-items-center">
                    <div style="max-width: 200px;" class="text-sm">
                        {{ slotProps.value.descricao }}
                    </div>
                </div>
                <span v-else>
                    <div style="max-width: 200px;" class="text-sm">
                        {{ slotProps.placeholder }}
                    </div>
                </span>
            </template>
            <template #option="slotProps">
                <div style="max-width: 30rem;" class="flex align-items-center w-full text-sm">
                    <p class="text-sm m-0 white-space-normal">
                        {{ slotProps.option.descricao }}
                    </p>
                </div>
            </template>
        </Dropdown>
        <SmallError :error="error" />
    </div>
</template>
